import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ArrowLeft, Loader2, Send, Phone, Headset } from 'lucide-react';
import { VoiceInput } from '../../components/VoiceInput';
import { toast } from 'sonner';

/**
 * Client ↔ iEvntür Support conversation. One canonical thread per company.
 * Near real-time (8s auto-refresh), plus a tap-to-call support line.
 */
const AdminSupportChat = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [supportPhone, setSupportPhone] = useState('404-482-0018');
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const load = useCallback(async (spin = true) => {
    if (spin) setLoading(true);
    try {
      const r = await api.get('/client/messages/admin/thread');
      setMessages(r.data.messages || []);
      if (r.data.support_phone) setSupportPhone(r.data.support_phone);
    } catch {
      if (spin) toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(true); }, [load]);

  useEffect(() => {
    const id = setInterval(() => load(false), 8000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async () => {
    const content = draft.trim();
    if (!content) return;
    setSending(true);
    try {
      await api.post('/client/messages/admin/send', { content });
      setDraft('');
      await load(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const telHref = `tel:+1${(supportPhone || '').replace(/[^0-9]/g, '')}`;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="client-admin-support-page">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/client/messages')} data-testid="support-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center">
              <Headset className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900 leading-tight">iEvntür Support</h1>
              <p className="text-xs text-gray-500">We usually reply within a few hours</p>
            </div>
          </div>
          <a href={telHref} className="ml-auto" data-testid="support-call-btn">
            <Button variant="outline" size="sm" className="border-blue-300 text-blue-700 hover:bg-blue-50">
              <Phone className="w-4 h-4 mr-1.5" /> {supportPhone}
            </Button>
          </a>
        </div>
      </header>

      <div className="flex-1 max-w-3xl w-full mx-auto px-4 py-6 overflow-y-auto" data-testid="support-messages">
        {loading ? (
          <div className="grid place-items-center py-16"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>
        ) : messages.length === 0 ? (
          <div className="text-center py-16 text-gray-500" data-testid="support-empty">
            <Headset className="w-10 h-10 text-blue-300 mx-auto mb-3" />
            <p className="font-semibold text-gray-800">Message the iEvntür team</p>
            <p className="text-sm mt-1">Ask about bookings, billing, staffing — anything. Or call us at {supportPhone}.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {messages.map((m) => (
              <div key={m.id} className={`flex ${m.from_client ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-sm ${m.from_client ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-800'}`}>
                  {!m.from_client && <div className="text-[10px] font-semibold text-blue-600 mb-0.5">{m.sender_name}</div>}
                  {m.content}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>
        )}
      </div>

      <div className="bg-white border-t border-gray-200">
        <div className="max-w-3xl mx-auto px-4 py-3 flex gap-2">
          <VoiceInput onTranscript={(t) => setDraft(t)} disabled={sending} />
          <Input value={draft} onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); send(); } }}
            placeholder="Type your message…" data-testid="support-input" />
          <Button onClick={send} disabled={sending || !draft.trim()} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="support-send-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminSupportChat;
